import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail } from "lucide-react";
import { SITE, IMAGES } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { TrackedLink } from "@/components/TrackedLinks";
import { FacebookIcon, InstagramIcon } from "@/components/SocialIcons";
import { navItems } from "@/components/navItems";

const footerNav = navItems.filter((i) => i.to !== "/");
const ghostCls = "!px-0 !py-0 !text-hacienda-cream/80 hover:!text-hacienda-terracotta !justify-start !items-start !rounded-none";

const FooterBrand = () => {
  const { t } = useLang();
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <img src={IMAGES.logo} alt="" width="64" height="64" loading="lazy" className="h-16 w-16 object-contain rounded-full bg-white" />
        <div>
          <p className="font-serif text-2xl font-semibold leading-none">Tijuas Tacos</p>
          <p className="text-hacienda-amber text-xs uppercase tracking-[0.25em] mt-1">Birrieria</p>
        </div>
      </div>
      <p className="text-hacienda-cream/80 leading-relaxed">{t("hero.eyebrow")}. {t("info.takeout")}.</p>
      <div className="flex items-center gap-3 mt-6">
        <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" className="bg-white/10 p-3 rounded-full hover:bg-hacienda-terracotta transition-colors" data-testid="footer-facebook-link" aria-label="Tijuas Tacos on Facebook"><FacebookIcon size={18} /></a>
        <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" className="bg-white/10 p-3 rounded-full hover:bg-hacienda-terracotta transition-colors" data-testid="footer-instagram-link" aria-label="Tijuas Tacos on Instagram"><InstagramIcon size={18} /></a>
      </div>
    </div>
  );
};

const FooterHours = () => {
  const { t } = useLang();
  return (
    <div>
      <h2 className="font-serif text-xl font-semibold mb-6 flex items-center gap-2"><Clock size={18} className="text-hacienda-terracotta" aria-hidden="true" />{t("footer.hours")}</h2>
      <dl className="space-y-2 text-hacienda-cream/80 text-sm">
        <div className="flex justify-between gap-4"><dt>{t("days.tueThu")}</dt><dd>11:00 AM – 9:00 PM</dd></div>
        <div className="flex justify-between gap-4"><dt>{t("days.friSat")}</dt><dd>11:00 AM – 10:00 PM</dd></div>
        <div className="flex justify-between gap-4"><dt>{t("days.sunMon")}</dt><dd className="text-hacienda-amber">{t("days.closed")}</dd></div>
      </dl>
    </div>
  );
};

const FooterContact = () => {
  const { t } = useLang();
  return (
    <div>
      <h2 className="font-serif text-xl font-semibold mb-6">{t("footer.contact")}</h2>
      <address className="not-italic space-y-4 text-hacienda-cream/80 text-sm">
        <TrackedLink href={SITE.directionsUrl} event="directions_click" params={{ location: "footer" }} variant="ghost" size="sm" className={ghostCls} data-testid="footer-address">
          <MapPin size={18} className="text-hacienda-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>{SITE.address.street}<br />{SITE.address.city}, {SITE.address.state} {SITE.address.zip}</span>
        </TrackedLink>
        <TrackedLink href={`tel:${SITE.phoneE164}`} event="phone_click" params={{ location: "footer" }} external={false} variant="ghost" size="sm" className={ghostCls} data-testid="footer-phone">
          <Phone size={18} className="text-hacienda-terracotta" aria-hidden="true" />{SITE.phoneDisplay}
        </TrackedLink>
        <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-hacienda-terracotta transition-colors" data-testid="footer-email">
          <Mail size={18} className="text-hacienda-terracotta" aria-hidden="true" />{SITE.email}
        </a>
      </address>
    </div>
  );
};

const FooterLinks = () => {
  const { t } = useLang();
  return (
    <div>
      <h2 className="font-serif text-xl font-semibold mb-6">{t("footer.quick")}</h2>
      <nav className="space-y-3 text-sm" aria-label="Footer">
        <a href={SITE.orderUrl} target="_blank" rel="noopener noreferrer" className="block text-hacienda-amber font-semibold hover:text-white transition-colors" data-testid="footer-order-link">{t("actions.order")}</a>
        {footerNav.map((item) => (
          <Link key={item.to} to={item.to} className="block text-hacienda-cream/80 hover:text-hacienda-terracotta transition-colors">{t(`nav.${item.key}`)}</Link>
        ))}
        <Link to="/privacy-policy" className="block text-hacienda-cream/80 hover:text-hacienda-terracotta transition-colors" data-testid="footer-privacy-link">{t("nav.privacy")}</Link>
        <a href={SITE.appStoreUrl} target="_blank" rel="noopener noreferrer" className="block text-hacienda-cream/80 hover:text-hacienda-terracotta transition-colors" data-testid="footer-app-link">iPhone App</a>
      </nav>
    </div>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-hacienda-charcoal text-hacienda-white pt-16 pb-24 md:pb-16" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
          <FooterBrand />
          <FooterHours />
          <FooterContact />
          <FooterLinks />
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-hacienda-cream/60 text-sm">© 2026 {SITE.legalName}. {t("footer.rights")}</p>
          <p className="text-hacienda-cream/60 text-sm italic text-center sm:text-right max-w-md">{SITE.scripture}</p>
        </div>
      </div>
    </footer>
  );
};
